import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

export interface StockAvailability {
  productId: string;
  available: boolean;
  insufficientIngredients: {
    ingredientId: string;
    ingredientName: string;
    required: number;
    available: number;
    unit: string;
  }[];
}

interface ProductIngredientCheck {
  product_id: string;
  ingredient_id: string;
  quantity: number;
  ingredient: {
    id: string;
    name: string;
    current_stock: number;
    unit: string;
  };
}

export function useStockAvailability() {
  const { activeRestaurant } = useRestaurantContext();
  const [productStockMap, setProductStockMap] = useState<Map<string, StockAvailability>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const checkProductAvailability = useCallback(async (
    productId: string,
    quantity: number = 1,
    product?: any
  ): Promise<StockAvailability> => {
    try {
      // Check if product tracks its own stock
      if (product?.tracks_stock) {
        const currentStock = Number(product.current_stock) || 0;
        if (currentStock < quantity) {
          return {
            productId,
            available: false,
            insufficientIngredients: [{
              ingredientId: productId,
              ingredientName: product.name || "Produto",
              required: quantity,
              available: currentStock,
              unit: "un",
            }],
          };
        }
        return { productId, available: true, insufficientIngredients: [] };
      }

      // Otherwise check recipe ingredients
      const { data, error } = await supabase
        .from("product_ingredients" as any)
        .select(`
          product_id,
          ingredient_id,
          quantity,
          ingredient:ingredients(id, name, current_stock, unit)
        `)
        .eq("product_id", productId);

      if (error) {
        console.error("Error checking stock availability:", error);
        return { productId, available: true, insufficientIngredients: [] };
      }

      if (!data || data.length === 0) {
        // No recipe defined, assume available
        return { productId, available: true, insufficientIngredients: [] };
      }

      const insufficientIngredients: StockAvailability["insufficientIngredients"] = [];

      for (const item of data as unknown as ProductIngredientCheck[]) {
        const requiredQty = item.quantity * quantity;
        const availableQty = item.ingredient?.current_stock || 0;

        if (availableQty < requiredQty) {
          insufficientIngredients.push({
            ingredientId: item.ingredient_id,
            ingredientName: item.ingredient?.name || "Desconhecido",
            required: requiredQty,
            available: availableQty,
            unit: item.ingredient?.unit || "",
          });
        }
      }

      return {
        productId,
        available: insufficientIngredients.length === 0,
        insufficientIngredients,
      };
    } catch (error) {
      console.error("Error checking stock:", error);
      return { productId, available: true, insufficientIngredients: [] };
    }
  }, []);

  const loadAllProductsAvailability = useCallback(async (productIds: string[], products?: any[]) => {
    if (!activeRestaurant || productIds.length === 0) return;

    setIsLoading(true);
    try {
      const newMap = new Map<string, StockAvailability>();

      // Handle products that track their own stock
      const productsWithOwnStock = products?.filter(p => p.tracks_stock) || [];
      for (const product of productsWithOwnStock) {
        const currentStock = Number(product.current_stock) || 0;
        const available = currentStock > 0;
        newMap.set(product.id, {
          productId: product.id,
          available,
          insufficientIngredients: available ? [] : [{
            ingredientId: product.id,
            ingredientName: product.name,
            required: 1,
            available: currentStock,
            unit: "un",
          }],
        });
      }

      // Get IDs of products that don't track their own stock (use recipes)
      const productsWithRecipeIds = productIds.filter(
        id => !productsWithOwnStock.find(p => p.id === id)
      );

      if (productsWithRecipeIds.length > 0) {
        // Fetch all product ingredients at once
        const { data, error } = await supabase
          .from("product_ingredients" as any)
          .select(`
            product_id,
            ingredient_id,
            quantity,
            ingredient:ingredients(id, name, current_stock, unit)
          `)
          .in("product_id", productsWithRecipeIds);

        if (error) {
          console.error("Error loading stock availability:", error);
          return;
        }

        // Group by product
        const productIngredients = new Map<string, ProductIngredientCheck[]>();
        for (const item of (data as unknown as ProductIngredientCheck[]) || []) {
          const existing = productIngredients.get(item.product_id) || [];
          existing.push(item);
          productIngredients.set(item.product_id, existing);
        }

        // Check each product
        for (const productId of productsWithRecipeIds) {
          const ingredients = productIngredients.get(productId) || [];
          
          if (ingredients.length === 0) {
            // No recipe, assume available
            newMap.set(productId, { productId, available: true, insufficientIngredients: [] });
            continue;
          }

          const insufficientIngredients: StockAvailability["insufficientIngredients"] = [];

          for (const item of ingredients) {
            const requiredQty = item.quantity;
            const availableQty = item.ingredient?.current_stock || 0;

            if (availableQty < requiredQty) {
              insufficientIngredients.push({
                ingredientId: item.ingredient_id,
                ingredientName: item.ingredient?.name || "Desconhecido",
                required: requiredQty,
                available: availableQty,
                unit: item.ingredient?.unit || "",
              });
            }
          }

          newMap.set(productId, {
            productId,
            available: insufficientIngredients.length === 0,
            insufficientIngredients,
          });
        }
      }

      setProductStockMap(newMap);
    } catch (error) {
      console.error("Error loading stock availability:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const getProductAvailability = useCallback((productId: string): StockAvailability | null => {
    return productStockMap.get(productId) || null;
  }, [productStockMap]);

  const isProductAvailable = useCallback((productId: string): boolean => {
    const availability = productStockMap.get(productId);
    return availability?.available ?? true;
  }, [productStockMap]);

  return {
    productStockMap,
    isLoading,
    checkProductAvailability,
    loadAllProductsAvailability,
    getProductAvailability,
    isProductAvailable,
  };
}
